import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField, 
  Checkbox, 
  SelectChangeEvent,
  Stack,
  ListItemText,
  InputAdornment
} from '@mui/material';
import { formatCurrencyInput } from '../utils/formatters';

interface FormData {
  objetivo: string;
  formato: string;
  plataforma: string;
  estrategia: string;
  target: string;
  idade: string[];
  interesse: string;
  praca: string;
  criativo: string;
  tipoCompra: string;
  ctr: string;
  tabelaPreco: string;
  investimentoMinimo: string;
  valorMinimoMensal: string;
  isValid?: boolean;
}

interface ConfiguracoesTecnicasEspeciaisProps {
  bvChecked: boolean;
  enabled: boolean;
  onValorMinimoLinhaChange?: (valor: string) => void;
  onChange?: (formData: FormData) => void;
}

const LINHAS = [
  { objetivo: 'Awareness', formato: 'CTV', plataforma: 'ShowHeroes', estrategia: 'CTV LG + TCL - Brasil', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 150,00', investimentoMinimo: 'R$ 45.000,00' },
  { objetivo: 'Awareness', formato: 'CTV', plataforma: 'Samsung', estrategia: 'CTV Samsung TV Plus  - Brasil', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 150,00', investimentoMinimo: 'R$ 45.000,00' },
  { objetivo: 'Awareness', formato: 'CTV', plataforma: 'Samsung', estrategia: 'CTV Samsung TV Plus  - Estados e Grandes Metrópoles', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 180,00', investimentoMinimo: 'R$ 45.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'ShowHeroes', estrategia: 'OTT - Brasil', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 120,00', investimentoMinimo: 'R$ 45.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Pluto TV / UOL', estrategia: 'OTT - Pluto TV', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 120,00', investimentoMinimo: 'R$ 45.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Direto', estrategia: 'Rotativo - Aberto BR', criativo: 'Vídeo 30s', tipoCompra: 'CPM', tabelaPreco: 'R$ 175,00', investimentoMinimo: 'R$ 300.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Direto', estrategia: 'Gênero do Conteúdo (mínimo 3)', criativo: 'Vídeo 30s', tipoCompra: 'CPM', tabelaPreco: 'R$ 201,25', investimentoMinimo: 'R$ 300.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Direto', estrategia: 'Top 10', criativo: 'Vídeo 30s', tipoCompra: 'CPM', tabelaPreco: 'R$ 245,00', investimentoMinimo: 'R$ 300.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Direto', estrategia: 'Idade e Gênero (binário)', criativo: 'Vídeo 30s', tipoCompra: 'CPM', tabelaPreco: 'R$ 220,00', investimentoMinimo: 'R$ 300.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Direto', estrategia: 'Geoloc (Apenas Estado)', criativo: 'Vídeo 30s', tipoCompra: 'CPM', tabelaPreco: 'R$ 192,50', investimentoMinimo: 'R$ 300.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Direto', estrategia: 'Primeira Impressão', criativo: 'Vídeo 30s', tipoCompra: 'CPM', tabelaPreco: 'R$ 350,00', investimentoMinimo: 'R$ 300.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Prog', estrategia: 'Aberto BR', criativo: 'Vídeo 10s', tipoCompra: 'CPM', tabelaPreco: 'R$ 116,00', investimentoMinimo: 'R$ 150.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Prog', estrategia: 'Aberto BR', criativo: 'Vídeo 15s', tipoCompra: 'CPM', tabelaPreco: 'R$ 154,67', investimentoMinimo: 'R$ 150.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Prog', estrategia: 'Aberto BR + Gênero de Conteúdo', criativo: 'Vídeo 10s', tipoCompra: 'CPM', tabelaPreco: 'R$ 127,60', investimentoMinimo: 'R$ 150.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Prog', estrategia: 'Aberto BR + Gênero de Conteúdo', criativo: 'Vídeo 15s', tipoCompra: 'CPM', tabelaPreco: 'R$ 170,13', investimentoMinimo: 'R$ 150.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Prog', estrategia: 'Estado ou Grandes Metrópoles', criativo: 'Vídeo 10s', tipoCompra: 'CPM', tabelaPreco: 'R$ 127,60', investimentoMinimo: 'R$ 150.000,00' },
  { objetivo: 'Awareness', formato: 'OTT', plataforma: 'Netflix Prog', estrategia: 'Estado ou Grandes Metrópoles', criativo: 'Vídeo 15s', tipoCompra: 'CPM', tabelaPreco: 'R$ 170,13', investimentoMinimo: 'R$ 150.000,00' },
  { objetivo: 'Awareness', formato: 'WhatsApp', plataforma: 'WhatsNow', estrategia: 'Abaixo de 20 mil disparos', criativo: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', tabelaPreco: 'R$ 0,77', tabelaPrecoBV: 'R$ 0,92', investimentoMinimo: 'R$10.000,00' },
  { objetivo: 'Awareness', formato: 'WhatsApp', plataforma: 'WhatsNow', estrategia: '20 mil a 30 mil disparos', criativo: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', tabelaPreco: 'R$ 0,70', tabelaPrecoBV: 'R$ 0,84', investimentoMinimo: 'R$10.000,00' },
  { objetivo: 'Awareness', formato: 'WhatsApp', plataforma: 'WhatsNow', estrategia: '31 mil a 200 mil disparos', criativo: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', tabelaPreco: 'R$ 0,53', tabelaPrecoBV: 'R$ 0,64', investimentoMinimo: 'R$10.000,00' },
  { objetivo: 'Awareness', formato: 'WhatsApp', plataforma: 'WhatsNow', estrategia: '201 mil a 600 mil disparos', criativo: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', tabelaPreco: 'R$ 0,50', tabelaPrecoBV: 'R$ 0,60', investimentoMinimo: 'R$10.000,00' },
  { objetivo: 'Awareness', formato: 'WhatsApp', plataforma: 'WhatsNow', estrategia: 'A partir de 600 mil disparos', criativo: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', tabelaPreco: 'R$ 0,43', tabelaPrecoBV: 'R$ 0,52', investimentoMinimo: 'R$10.000,00' },
  { objetivo: 'Awareness', formato: 'Áudio', plataforma: 'Spotify', estrategia: 'Segmentação demográfica e geográfica', criativo: 'Display', tipoCompra: 'CPM', tabelaPreco: 'R$ 49,20', tabelaPrecoBV: 'R$ 59,04', investimentoMinimo: 'R$ 25.000,00' },
  { objetivo: 'Awareness', formato: 'Áudio', plataforma: 'Spotify', estrategia: 'Segmentação demográfica e geográfica', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 150,37', tabelaPrecoBV: 'R$ 180,44', investimentoMinimo: 'R$ 25.000,00' },
  { objetivo: 'Awareness', formato: 'Áudio', plataforma: 'Spotify', estrategia: 'Segmentação demográfica e geográfica', criativo: 'Audio', tipoCompra: 'CPM', tabelaPreco: 'R$ 138,00', tabelaPrecoBV: 'R$ 165,60', investimentoMinimo: 'R$ 25.000,00' },
  { objetivo: 'Awareness', formato: 'Display', plataforma: 'Serasa', estrategia: 'Site & App Serasa', criativo: 'Display', tipoCompra: 'CPM', tabelaPreco: 'R$ 95,00', tabelaPrecoBV: 'R$ 110,00', investimentoMinimo: 'R$ 15.000,00' },
  { objetivo: 'Consideração', formato: 'Push Notification', plataforma: 'Siprocal', estrategia: 'Push Notification (Contextual ou App Target) Com Imagem ou Apenas Texto', criativo: 'Display, Logo + Texto', tipoCompra: 'CPC', tabelaPreco: 'R$ 2,67', tabelaPrecoBV: 'R$ 3,20', investimentoMinimo: 'R$ 30.000,00' },
  { objetivo: 'Consideração', formato: 'Push Notification', plataforma: 'Hands', estrategia: 'Hands - Push Notification - Geotrack & Livetrack (Raios mais precisos em lista de endereços)', criativo: 'Display, Logo + Texto', tipoCompra: 'CPE', tabelaPreco: 'R$ 0,70', tabelaPrecoBV: 'R$ 0,85', investimentoMinimo: 'R$ 30.000,00' },
  { objetivo: 'Consideração', formato: 'Push Notification', plataforma: 'Hands', estrategia: 'Hands - Push Notification - Geotrack (Raios mais precisos em lista de endereços) + Relatório de Store Visits', criativo: 'Display, Logo + Texto', tipoCompra: 'CPE', tabelaPreco: 'R$ 1,00', tabelaPrecoBV: 'R$ 1,25', investimentoMinimo: 'R$ 30.000,00' },
  { objetivo: 'Consideração', formato: 'Web Push', plataforma: 'Pixel Roads', estrategia: 'País/Estado/Grandes Metrópoles', criativo: 'Display + Texto', tipoCompra: 'CPC', tabelaPreco: 'R$ 1,50', investimentoMinimo: 'R$ 15.000,00' },
  { objetivo: 'Consideração', formato: 'Web Push', plataforma: 'Pixel Roads', estrategia: 'Pequenas Praças - Idealmente trabalhar com mais de uma', criativo: 'Display + Texto', tipoCompra: 'CPC', tabelaPreco: 'R$ 1,85', investimentoMinimo: 'R$ 15.000,00' },
  { objetivo: 'Consideração', formato: 'Native', plataforma: 'Pixel Roads', estrategia: 'Native 3.0', criativo: 'Native - Imagem + Texto', tipoCompra: 'CPC', tabelaPreco: 'R$ 1,83', tabelaPrecoBV: 'R$ 2,20', investimentoMinimo: 'R$ 15.000,00' },
  { objetivo: 'Consideração', formato: 'Native', plataforma: 'Tinder', estrategia: 'Native Display Card (Square) - DV360 - PD', criativo: 'Display', tipoCompra: 'CPM', tabelaPreco: 'R$ 150,00', tabelaPrecoBV: 'R$ 204,23', investimentoMinimo: 'R$ 30.000,00' },
  { objetivo: 'Consideração', formato: 'Native', plataforma: 'Tinder', estrategia: 'Native Display Card - DV360 - PG', criativo: 'Display', tipoCompra: 'CPM', tabelaPreco: 'R$ 150,00', tabelaPrecoBV: 'R$ 204,23', investimentoMinimo: 'R$ 105.000,00' },
  { objetivo: 'Consideração', formato: 'Native', plataforma: 'Tinder', estrategia: 'Native Video Card - DV360 - PG', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 150,00', tabelaPrecoBV: 'R$ 204,23', investimentoMinimo: 'R$ 105.000,00' },
  { objetivo: 'Consideração', formato: 'Alto Impacto', plataforma: 'Seedtag', estrategia: 'Contextual Impact Display (CID)', criativo: 'Display', tipoCompra: 'CPM', tabelaPreco: 'R$ 75,60', tabelaPrecoBV: 'R$ 90,72', investimentoMinimo: 'R$ 34.000,00' },
  { objetivo: 'Consideração', formato: 'Alto Impacto', plataforma: 'Seedtag', estrategia: 'Contextual Engagement Display (CED)', criativo: 'Display', tipoCompra: 'CPM', tabelaPreco: 'R$ 73,07', tabelaPrecoBV: 'R$ 87,68', investimentoMinimo: 'R$ 34.000,00' },
  { objetivo: 'Consideração', formato: 'Alto Impacto', plataforma: 'Seedtag', estrategia: 'Contextual Frame Display (CFD)', criativo: 'Display', tipoCompra: 'CPM', tabelaPreco: 'R$ 94,50', tabelaPrecoBV: 'R$ 113,40', investimentoMinimo: 'R$ 34.000,00' },
  { objetivo: 'Consideração', formato: 'Alto Impacto', plataforma: 'Seedtag', estrategia: 'Contextual Branded Video (CBV)', criativo: 'Vídeo', tipoCompra: 'CPV', tabelaPreco: 'R$ 0,60', tabelaPrecoBV: 'R$ 0,72', investimentoMinimo: 'R$ 34.000,00' },
  { objetivo: 'Consideração', formato: 'Alto Impacto', plataforma: 'Seedtag', estrategia: 'Contextual Engagement Video (CEV)', criativo: 'Vídeo', tipoCompra: 'CPV', tabelaPreco: 'R$ 0,77', tabelaPrecoBV: 'R$ 0,92', investimentoMinimo: 'R$ 34.000,00' },
  { objetivo: 'Consideração', formato: 'Alto Impacto', plataforma: 'Seedtag', estrategia: 'Contextual Outstream Video (COV)', criativo: 'Vídeo', tipoCompra: 'CPV', tabelaPreco: 'R$ 0,60', tabelaPrecoBV: 'R$ 0,72', investimentoMinimo: 'R$ 34.000,00' },
  { objetivo: 'Consideração', formato: 'Alto Impacto', plataforma: 'Seedtag', estrategia: 'Contextual Native', criativo: 'Display', tipoCompra: 'CPC', tabelaPreco: 'R$ 9,60', tabelaPrecoBV: 'R$ 11,52', investimentoMinimo: 'R$ 34.000,00' },
  { objetivo: 'Consideração', formato: 'Alto Impacto', plataforma: 'Vidoomy', estrategia: 'Vídeo Enriquecido', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 90,00', tabelaPrecoBV: 'R$ 130,00', investimentoMinimo: 'R$ 100.000,00' },
  { objetivo: 'Consideração', formato: 'In Game', plataforma: 'Etermax', estrategia: 'Playable Ads', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 208,01', investimentoMinimo: 'R$ 105.000,00' },
  { objetivo: 'Consideração', formato: 'In Game', plataforma: 'Etermax', estrategia: 'Interstitial', criativo: 'Display ou Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 79,42', investimentoMinimo: 'R$ 20.000,00' },
  { objetivo: 'Consideração', formato: 'In Game', plataforma: 'Etermax', estrategia: 'Rewarded Vídeo', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 147,50', investimentoMinimo: 'R$ 20.000,00' },
  { objetivo: 'Consideração', formato: 'In Game', plataforma: 'Etermax', estrategia: 'Banner', criativo: 'Display', tipoCompra: 'CPM', tabelaPreco: 'R$ 45,38', investimentoMinimo: 'R$ 20.000,00' },
  { objetivo: 'Consideração', formato: 'In Game', plataforma: 'Adsmovil', estrategia: 'Interstitial', criativo: 'Display ou Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 60,00', tabelaPrecoBV: 'R$ 80,00', investimentoMinimo: 'R$ 35.000,00' },
  { objetivo: 'Consideração', formato: 'In Game', plataforma: 'Adsmovil', estrategia: 'Rewarded Vídeo', criativo: 'Vídeo', tipoCompra: 'CPM', tabelaPreco: 'R$ 120,00', tabelaPrecoBV: 'R$ 160,00', investimentoMinimo: 'R$ 35.000,00' },
];

const getUnique = (arr: any[], key: string) => Array.from(new Set(arr.map(item => item[key])));

const ConfiguracoesTecnicasEspeciais: React.FC<ConfiguracoesTecnicasEspeciaisProps> = ({ bvChecked, enabled, onValorMinimoLinhaChange, onChange }) => {
  const [formData, setFormData] = useState({
    objetivo: '',
    formato: '',
    plataforma: '',
    estrategia: '',
    target: '',
    idade: [] as string[],
    interesse: '',
    praca: '',
    criativo: '',
    tipoCompra: '',
    ctr: '',
    tabelaPreco: '',
    investimentoMinimo: '',
    valorMinimoMensal: ''
  });

  // Memoizar as filtragens para evitar recálculos desnecessários
  const linhasFormato = useMemo(() => 
    LINHAS.filter(l => !formData.objetivo || l.objetivo === formData.objetivo),
    [formData.objetivo]
  );

  const linhasPlataforma = useMemo(() => 
    LINHAS.filter(l =>
      (!formData.objetivo || l.objetivo === formData.objetivo) &&
      (!formData.formato || l.formato === formData.formato)
    ),
    [formData.objetivo, formData.formato]
  );

  const linhasEstrategia = useMemo(() => 
    LINHAS.filter(l =>
      (!formData.objetivo || l.objetivo === formData.objetivo) &&
      (!formData.formato || l.formato === formData.formato) &&
      (!formData.plataforma || l.plataforma === formData.plataforma)
    ),
    [formData.objetivo, formData.formato, formData.plataforma]
  );

  const linhasCriativo = useMemo(() => 
    LINHAS.filter(l =>
      (!formData.objetivo || l.objetivo === formData.objetivo) &&
      (!formData.formato || l.formato === formData.formato) &&
      (!formData.plataforma || l.plataforma === formData.plataforma) &&
      (!formData.estrategia || l.estrategia === formData.estrategia)
    ),
    [formData.objetivo, formData.formato, formData.plataforma, formData.estrategia]
  );

  const linhasTipoCompra = useMemo(() => 
    LINHAS.filter(l =>
      (!formData.objetivo || l.objetivo === formData.objetivo) &&
      (!formData.formato || l.formato === formData.formato) &&
      (!formData.plataforma || l.plataforma === formData.plataforma) &&
      (!formData.estrategia || l.estrategia === formData.estrategia) &&
      (!formData.criativo || l.criativo === formData.criativo)
    ),
    [formData.objetivo, formData.formato, formData.plataforma, formData.estrategia, formData.criativo]
  );

  // Memoizar as opções dos selects
  const formatos = useMemo(() => getUnique(linhasFormato, 'formato'), [linhasFormato]);
  const plataformas = useMemo(() => getUnique(linhasPlataforma, 'plataforma'), [linhasPlataforma]);
  const estrategias = useMemo(() => getUnique(linhasEstrategia, 'estrategia'), [linhasEstrategia]);
  const criativos = useMemo(() => getUnique(linhasCriativo, 'criativo'), [linhasCriativo]);
  const tiposCompra = useMemo(() => getUnique(linhasTipoCompra, 'tipoCompra'), [linhasTipoCompra]);

  // Memoizar a linha selecionada
  const linhaSelecionada = useMemo(() => {
    if (!formData.objetivo || !formData.formato || !formData.plataforma || 
        !formData.estrategia || !formData.criativo || !formData.tipoCompra) {
      return null;
    }
    
    return LINHAS.find(l =>
      l.objetivo === formData.objetivo &&
      l.formato === formData.formato &&
      l.plataforma === formData.plataforma &&
      l.estrategia === formData.estrategia &&
      l.criativo === formData.criativo &&
      l.tipoCompra === formData.tipoCompra
    ) || null;
  }, [formData.objetivo, formData.formato, formData.plataforma, formData.estrategia, formData.criativo, formData.tipoCompra]);

  // Função otimizada para lidar com mudanças nos selects
  const handleSelectChange = useCallback((e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData: any = { ...prev };
      newData[name] = value;

      // Define field dependencies
      const dependencies: Record<string, string[]> = {
        objetivo: ['formato', 'plataforma', 'estrategia', 'target', 'idade', 'interesse', 'praca', 'criativo', 'tipoCompra'],
        formato: ['plataforma', 'estrategia', 'target', 'idade', 'interesse', 'praca', 'criativo', 'tipoCompra'],
        plataforma: ['estrategia', 'target', 'idade', 'interesse', 'praca', 'criativo', 'tipoCompra'],
        estrategia: ['target', 'idade', 'interesse', 'praca', 'criativo', 'tipoCompra'],
        target: ['idade', 'interesse', 'praca', 'criativo', 'tipoCompra'],
        idade: ['interesse', 'praca', 'criativo', 'tipoCompra'],
        interesse: ['praca', 'criativo', 'tipoCompra'],
        praca: ['criativo', 'tipoCompra'],
        criativo: ['tipoCompra'],
        tipoCompra: []
      };

      // Clear dependent fields
      const fieldsToClear = dependencies[name] || [];
      fieldsToClear.forEach(field => {
        newData[field] = field === 'idade' ? [] : '';
      });

      return newData;
    });
  }, []);

  // Handler específico para o campo idade (multiple select)
  const handleIdadeChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      idade: Array.isArray(value) ? value : [],
      interesse: '',
      praca: '',
      criativo: '',
      tipoCompra: ''
    }));
  }, []);

  // Ref para controlar se já processamos o valor atual
  const valorProcessadoRef = useRef<string>('');
  
  // Ref para armazenar a função onChange
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  
  // Ref para armazenar a função onValorMinimoLinhaChange
  const onValorMinimoLinhaChangeRef = useRef(onValorMinimoLinhaChange);
  onValorMinimoLinhaChangeRef.current = onValorMinimoLinhaChange;
  useEffect(() => {
    if (linhaSelecionada && linhaSelecionada.investimentoMinimo) {
      // Extrair apenas o número, sem R$ e espaços
      const clean = (v: string) => v.replace(/^R\$\s*/, '').replace(/\./g, '').replace(',', '.').trim();
      const valorLimpo = clean(linhaSelecionada.investimentoMinimo);
      
      // Só atualizar se o valor for diferente do último processado
      if (valorProcessadoRef.current !== valorLimpo) {
        valorProcessadoRef.current = valorLimpo;
        
        // Atualizar o formData local
        setFormData(prev => ({ ...prev, investimentoMinimo: valorLimpo }));
        
              // Notificar o componente pai
      if (onValorMinimoLinhaChangeRef.current) {
        onValorMinimoLinhaChangeRef.current(valorLimpo);
      }
    }
  } else {
    // Limpar o valor se não há linha selecionada
    if (valorProcessadoRef.current !== '') {
      valorProcessadoRef.current = '';
      setFormData(prev => ({ ...prev, investimentoMinimo: '' }));
      if (onValorMinimoLinhaChangeRef.current) {
        onValorMinimoLinhaChangeRef.current('');
      }
    }
  }
}, [linhaSelecionada]);

  // Atualizar tabelaPreco automaticamente conforme seleção
  useEffect(() => {
    // Só tenta buscar se todos os campos necessários estiverem preenchidos
    if (
      formData.objetivo &&
      formData.formato &&
      formData.plataforma &&
      formData.estrategia &&
      formData.criativo &&
      formData.tipoCompra
    ) {
      const match = LINHAS.find(l =>
        l.objetivo === formData.objetivo &&
        l.formato === formData.formato &&
        l.plataforma === formData.plataforma &&
        l.estrategia === formData.estrategia &&
        l.criativo === formData.criativo &&
        l.tipoCompra === formData.tipoCompra
      );
      let preco = '';
      if (match) {
        preco = bvChecked && match.tabelaPrecoBV ? match.tabelaPrecoBV : match.tabelaPreco;
      }
      if (preco && formData.tabelaPreco !== preco) {
        setFormData(prev => ({ ...prev, tabelaPreco: preco }));
      }
    } else if (formData.tabelaPreco) {
      setFormData(prev => ({ ...prev, tabelaPreco: '' }));
    }
  }, [formData.objetivo, formData.formato, formData.plataforma, formData.estrategia, formData.criativo, formData.tipoCompra, bvChecked]);

  // Resetar criativo se não estiver nas opções
  useEffect(() => {
    if (formData.criativo && !criativos.includes(formData.criativo)) {
      setFormData(prev => ({ ...prev, criativo: '' }));
    }
  }, [criativos, formData.criativo]);

  // Resetar tipoCompra se não estiver nas opções
  useEffect(() => {
    if (formData.tipoCompra && !tiposCompra.includes(formData.tipoCompra)) {
      setFormData(prev => ({ ...prev, tipoCompra: '' }));
    }
  }, [tiposCompra, formData.tipoCompra]);

  // Chamar onChange sempre que formData mudar (otimizado)
  useEffect(() => {
    if (onChangeRef.current) {
      // Validar se todos os campos visíveis estão preenchidos
      const camposObrigatorios = [
        'objetivo',
        'formato',
        'plataforma',
        'estrategia',
        'target',
        'criativo',
        'tipoCompra'
      ];

      // Adicionar campos condicionais
                if (formData.target) {
        camposObrigatorios.push('idade');
      }
      if (formData.idade && formData.idade.length > 0) {
        camposObrigatorios.push('interesse');
      }
      if (formData.interesse) {
        camposObrigatorios.push('praca');
      }
      if (!['OTT', 'CTV', 'WhatsApp', 'Display', 'Alto Impacto'].includes(formData.formato) && 
          !(formData.formato === 'Áudio' && 
            formData.plataforma === 'Spotify' && 
            ['Display', 'Audio'].includes(formData.criativo))) {
        camposObrigatorios.push('ctr');
      }

      // Verificar se todos os campos obrigatórios estão preenchidos
      const todosCamposPreenchidos = camposObrigatorios.every(campo => {
        if (campo === 'idade') {
          return formData.idade && formData.idade.length > 0;
        }
        return Boolean(formData[campo as keyof Omit<FormData, 'isValid'>]);
      });

      // Validar valor mínimo mensal
      const valorMinimoValido = Boolean(formData.investimentoMinimo && 
        formData.investimentoMinimo !== '0,00' && 
        formData.investimentoMinimo !== '');

      // Adicionar flag de validação ao formData
      onChangeRef.current({
        ...formData,
        isValid: todosCamposPreenchidos && valorMinimoValido
      });
    }
  }, [
    formData.objetivo,
    formData.formato,
    formData.plataforma,
    formData.estrategia,
    formData.target,
    formData.idade,
    formData.interesse,
    formData.praca,
    formData.criativo,
    formData.tipoCompra,
    formData.ctr,
    formData.investimentoMinimo
  ]);

  // Resetar campos quando o componente for desabilitado
  useEffect(() => {
    if (!enabled) {
      setFormData({
        objetivo: '',
        formato: '',
        plataforma: '',
        estrategia: '',
        target: '',
        idade: [],
        interesse: '',
        praca: '',
        criativo: '',
        tipoCompra: '',
        ctr: '',
        tabelaPreco: '',
        investimentoMinimo: '',
        valorMinimoMensal: ''
      });
    }
  }, [enabled]);

  return (
    <Box sx={{ p: 0, width: '100%', minWidth: 'max-content' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row', 
        flexWrap: 'nowrap', 
        gap: 1.5, 
        alignItems: 'flex-start',
        width: 'max-content'
      }}>
        <Box sx={{ minWidth: 120, flex: '0 0 140px' }}>
            <FormControl fullWidth disabled={!enabled}>
              <InputLabel>Objetivo</InputLabel>
              <Select
                value={formData.objetivo}
                label="Objetivo"
                name="objetivo"
                onChange={handleSelectChange}
              sx={{
                '& .MuiSelect-select': {
                  minHeight: '56px !important',
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
              >
                {getUnique(LINHAS, 'objetivo').map((objetivo) => (
                  <MenuItem key={objetivo} value={objetivo}>{objetivo}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        <Box sx={{ minWidth: 120, flex: '0 0 140px' }}>
            <FormControl fullWidth disabled={!enabled || !formData.objetivo}>
              <InputLabel>Formato</InputLabel>
              <Select
                value={formData.formato}
                label="Formato"
                name="formato"
                onChange={handleSelectChange}
              sx={{
                '& .MuiSelect-select': {
                  minHeight: '56px !important',
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
              >
                {formatos.map((formato) => (
                  <MenuItem key={formato} value={formato}>{formato}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        <Box sx={{ minWidth: 120, flex: '0 0 140px' }}>
            <FormControl fullWidth disabled={!enabled || !formData.formato}>
              <InputLabel>Plataforma</InputLabel>
              <Select
                value={formData.plataforma}
                label="Plataforma"
                name="plataforma"
                onChange={handleSelectChange}
              sx={{
                '& .MuiSelect-select': {
                  minHeight: '56px !important',
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
              >
                {plataformas.map((plataforma) => (
                  <MenuItem key={plataforma} value={plataforma}>{plataforma}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        <Box sx={{ minWidth: 160, flex: '0 0 180px' }}>
            <FormControl fullWidth disabled={!enabled || !formData.plataforma}>
              <InputLabel>Estratégia</InputLabel>
              <Select
                value={formData.estrategia}
                label="Estratégia"
                name="estrategia"
                onChange={handleSelectChange}
              sx={{
                '& .MuiSelect-select': {
                  minHeight: '56px !important',
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
              >
                {estrategias.map((estrategia) => (
                  <MenuItem key={estrategia} value={estrategia}>{estrategia}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        <Box sx={{ minWidth: 120, flex: '0 0 140px' }}>
            <FormControl fullWidth disabled={!enabled || !formData.estrategia}>
              <InputLabel>Target</InputLabel>
              <Select
                value={formData.target}
                label="Target"
                name="target"
                onChange={handleSelectChange}
              sx={{
                '& .MuiSelect-select': {
                  minHeight: '56px !important',
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
              >
                <MenuItem value="Homens">Homens</MenuItem>
                <MenuItem value="Mulheres">Mulheres</MenuItem>
                <MenuItem value="Ambos Sexos">Ambos Sexos</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {formData.target && (
          <Box sx={{ minWidth: 140, flex: '0 0 160px' }}>
              <FormControl fullWidth disabled={!enabled}>
                <InputLabel>Idade</InputLabel>
                <Select
                  value={formData.idade || []}
                  label="Idade"
                  name="idade"
                  onChange={handleIdadeChange}
                  multiple
                sx={{
                  '& .MuiSelect-select': {
                    minHeight: '56px !important',
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
                  renderValue={(selected) => {
                    const ages = selected as string[];
                    if (ages.length === 0) return '';
                    
                    if (formData.target === 'Ambos Sexos') {
                      const ageRanges = ages.map(age => {
                        const [min, max] = age.split('-');
                        return { min: parseInt(min), max: parseInt(max.replace('+', '')) };
                      });
                      
                      const minAge = Math.min(...ageRanges.map(range => range.min));
                      const maxAge = Math.max(...ageRanges.map(range => range.max));
                      
                      return `AS - ${minAge}-${maxAge}${maxAge === 65 ? '+' : ''}`;
                    } else {
                      const ageRanges = ages.map(age => {
                        const [min, max] = age.split('-');
                        return { min: parseInt(min), max: parseInt(max.replace('+', '')) };
                      });
                      
                      const minAge = Math.min(...ageRanges.map(range => range.min));
                      const maxAge = Math.max(...ageRanges.map(range => range.max));
                      
                      return `${formData.target} - ${minAge}-${maxAge}${maxAge === 65 ? '+' : ''}`;
                    }
                  }}
                >
                  <MenuItem value="18-24">
                    <Checkbox checked={formData.idade?.includes('18-24')} />
                    <ListItemText primary="18-24" />
                  </MenuItem>
                  <MenuItem value="25-34">
                    <Checkbox checked={formData.idade?.includes('25-34')} />
                    <ListItemText primary="25-34" />
                  </MenuItem>
                  <MenuItem value="35-44">
                    <Checkbox checked={formData.idade?.includes('35-44')} />
                    <ListItemText primary="35-44" />
                  </MenuItem>
                  <MenuItem value="45-54">
                    <Checkbox checked={formData.idade?.includes('45-54')} />
                    <ListItemText primary="45-54" />
                  </MenuItem>
                  <MenuItem value="55-65+">
                    <Checkbox checked={formData.idade?.includes('55-65+')} />
                    <ListItemText primary="55-65+" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        <Box sx={{ minWidth: 140, flex: '0 0 160px' }}>
            <TextField
              fullWidth
              required
              label="Interesse"
              value={formData.interesse}
              onChange={(e) => setFormData(prev => ({ ...prev, interesse: e.target.value }))}
              placeholder="Digite o interesse do público-alvo"
              multiline
              minRows={2}
              maxRows={10}
              disabled={!enabled || !formData.idade || formData.idade.length === 0}
              sx={{
              '& .MuiInputBase-root': {
                minHeight: '80px',
              },
                '&.Mui-disabled': {
                  pointerEvents: 'none',
                  backgroundColor: 'transparent',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                },
              }}
            />
          </Box>
        <Box sx={{ minWidth: 140, flex: '0 0 160px' }}>
            <TextField
              fullWidth
              required
              label="Praça"
              value={formData.praca}
              onChange={(e) => setFormData(prev => ({ ...prev, praca: e.target.value }))}
              placeholder="Digite a praça do público-alvo"
              multiline
              minRows={2}
              maxRows={10}
              disabled={!enabled || !formData.interesse}
              sx={{
              '& .MuiInputBase-root': {
                minHeight: '80px',
              },
                '&.Mui-disabled': {
                  pointerEvents: 'none',
                  backgroundColor: 'transparent',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                },
              }}
            />
          </Box>
        <Box sx={{ minWidth: 140, flex: '0 0 160px' }}>
            <TextField
              fullWidth
              label="Valor Mínimo Mensal (Especiais)"
              value={formatCurrencyInput(formData.valorMinimoMensal || '')}
              onChange={(e) => setFormData(prev => ({ ...prev, valorMinimoMensal: formatCurrencyInput(e.target.value) }))}
              type="text"
              placeholder="Digite o valor mínimo mensal"
              disabled={!enabled || !formData.praca}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: '56px',
                },
                '&.Mui-disabled': {
                  pointerEvents: 'none',
                  backgroundColor: 'transparent',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                },
              }}
            />
        </Box>
        <Box sx={{ minWidth: 140, flex: '0 0 160px' }}>
            <FormControl fullWidth disabled={!enabled || !formData.target || formData.idade.length === 0 || !formData.praca}>
              <InputLabel>Criativo</InputLabel>
              <Select
                value={formData.criativo}
                label="Criativo"
                name="criativo"
                onChange={handleSelectChange}
              sx={{
                '& .MuiSelect-select': {
                  minHeight: '56px !important',
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
              >
                {criativos.map((criativo) => (
                  <MenuItem key={criativo} value={criativo}>{criativo}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        <Box sx={{ minWidth: 120, flex: '0 0 140px' }}>
            <FormControl fullWidth disabled={!enabled || !formData.criativo}>
              <InputLabel>Tipo de Compra</InputLabel>
              <Select
                value={formData.tipoCompra}
                label="Tipo de Compra"
                name="tipoCompra"
                onChange={handleSelectChange}
              sx={{
                '& .MuiSelect-select': {
                  minHeight: '56px !important',
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
              >
                {tiposCompra.map((tipoCompra) => (
                  <MenuItem key={tipoCompra} value={tipoCompra}>{tipoCompra}</MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>
        {/* Campo de CTR/VTR - só mostra se não for OTT, CTV, WhatsApp, Spotify Audio Display/Audio, Display ou Alto Impacto */}
        {!['OTT', 'CTV', 'WhatsApp', 'Display', 'Alto Impacto'].includes(formData.formato) && 
         !(formData.formato === 'Áudio' && 
           formData.plataforma === 'Spotify' && 
           ['Display', 'Audio'].includes(formData.criativo)) && (
          <Box sx={{ minWidth: 120, flex: '0 0 140px' }}>
            <TextField
              fullWidth
              label={formData.formato === 'Áudio' && formData.plataforma === 'Spotify' && formData.criativo === 'Vídeo' ? 'VTR' : 'CTR'}
              value={formData.ctr}
              onChange={(e) => setFormData(prev => ({ ...prev, ctr: e.target.value }))}
              placeholder={`Digite o ${formData.formato === 'Áudio' && formData.plataforma === 'Spotify' && formData.criativo === 'Vídeo' ? 'VTR' : 'CTR'}`}
              disabled={!enabled || !formData.tipoCompra}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: '56px',
                },
                '&.Mui-disabled': {
                  pointerEvents: 'none',
                  backgroundColor: 'transparent',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ConfiguracoesTecnicasEspeciais; 